// Presentation boundary only. The server owns billing, chargeability and renewal.
export const CALENDAR_MONTH_POLICY = 'calendar_month_v1';
export const LESSON_CYCLE_POLICY = 'lesson_cycle_v1';

const provided = (value) => value !== undefined && value !== null && value !== '';
const readSnapshotField = (record, key) => {
  const values = [record?.[key], record?.planSnapshot?.[key]].filter(provided);
  if (values.length > 1 && values[0] !== values[1]) throw new Error('BILLING_POLICY_SNAPSHOT_CONFLICT');
  return values[0];
};
const positiveInteger = (value, max) => typeof value === 'number'
  && Number.isSafeInteger(value) && value >= 1 && value <= max;

// Never infer an agreement's policy from its code, monthly aliases, or its live plan.
export const subscriptionBillingPolicy = (record = {}) => {
  try {
    const version = readSnapshotField(record, 'billingPolicyVersion') || CALENDAR_MONTH_POLICY;
    const mode = readSnapshotField(record, 'billingMode');
    if (version === CALENDAR_MONTH_POLICY) {
      if (provided(mode) && !['calendar_month', 'monthly'].includes(mode)) throw new Error('BILLING_POLICY_MODE_MISMATCH');
      return { kind: 'calendar_month', billingPolicyVersion: version };
    }
    if (version !== LESSON_CYCLE_POLICY) throw new Error('UNSUPPORTED_BILLING_POLICY');
    if (mode !== 'lesson_cycle') throw new Error('BILLING_POLICY_MODE_MISMATCH');
    const lessonsPerCycle = readSnapshotField(record, 'lessonsPerCycle');
    const lessonsPerWeek = readSnapshotField(record, 'lessonsPerWeek');
    let pricePerCycle = readSnapshotField(record, 'pricePerCycle');
    let currency = readSnapshotField(record, 'currency');
    // The native hold DTO uses an immutable PLN minor-unit priceSnapshot.
    // Only that explicit shape may bridge to the catalogue's major units.
    if (record?.priceSnapshot) {
      const price = record.priceSnapshot;
      if (price.currency !== 'PLN' || !positiveInteger(price.amountMinor, Number.MAX_SAFE_INTEGER)
        || (provided(currency) && currency !== price.currency)
        || (provided(pricePerCycle) && pricePerCycle !== price.amountMinor / 100)) {
        throw new Error('BILLING_POLICY_SNAPSHOT_CONFLICT');
      }
      pricePerCycle = price.amountMinor / 100;
      currency = price.currency;
    }
    if (!positiveInteger(lessonsPerCycle, 124) || !positiveInteger(lessonsPerWeek, 12)) {
      throw new Error('INVALID_BILLING_POLICY_QUANTITY');
    }
    if (typeof pricePerCycle !== 'number' || !Number.isFinite(pricePerCycle) || pricePerCycle <= 0
      || typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) {
      throw new Error('INVALID_CYCLE_PRICE');
    }
    return { kind: 'lesson_cycle', billingPolicyVersion: version, billingMode: mode,
      lessonsPerCycle, lessonsPerWeek, pricePerCycle, currency };
  } catch (error) {
    return { kind: 'invalid', errorCode: error.message };
  }
};

export const isLessonCycle = (record) => subscriptionBillingPolicy(record).kind === 'lesson_cycle';
export const isSupportedBillingPolicy = (record) => subscriptionBillingPolicy(record).kind !== 'invalid';

export const matchingBillingPolicies = (plan, hold) => {
  const left = subscriptionBillingPolicy(plan), right = subscriptionBillingPolicy(hold);
  if (left.kind === 'invalid' || right.kind === 'invalid' || left.kind !== right.kind) return false;
  if (left.kind === 'calendar_month') return true;
  return ['billingPolicyVersion', 'billingMode', 'lessonsPerCycle', 'lessonsPerWeek', 'pricePerCycle', 'currency']
    .every((key) => left[key] === right[key]);
};

export const billingWeekdays = (record) => {
  const policy = subscriptionBillingPolicy(record);
  if (policy.kind === 'lesson_cycle') return policy.lessonsPerWeek;
  if (policy.kind === 'invalid') return null;
  return Number(record?.averageLessonsPerWeek ?? record?.weeklyFrequency ?? 1);
};

export const cycleCurrentView = (record) => {
  const policy = subscriptionBillingPolicy(record), view = record?.currentCycle;
  if (policy.kind !== 'lesson_cycle' || !view || typeof view !== 'object') return null;
  const fields = ['granted', 'reserved', 'available', 'billableUsed'];
  if (fields.some((field) => typeof view[field] !== 'number' || !Number.isSafeInteger(view[field]) || view[field] < 0)
    || view.billableUsed > policy.lessonsPerCycle || view.reserved + view.available > view.granted
    || typeof view.renewalStatus !== 'string' || !view.renewalStatus.trim()) return null;
  return { ...view, lessonsPerCycle: policy.lessonsPerCycle };
};

// These are UI language aliases, not browser Intl locale identifiers.
export const cycleIntlLocale = (language = 'pl') => {
  const base = String(language).toLowerCase().split('-')[0];
  return ({ pb: 'pt-BR', zt: 'zh-TW' })[base] || language || 'pl';
};
export const cyclePriceLabel = (record, language = 'pl') => {
  const policy = subscriptionBillingPolicy(record);
  if (policy.kind !== 'lesson_cycle') return null;
  try {
    return new Intl.NumberFormat(cycleIntlLocale(language), { style: 'currency', currency: policy.currency })
      .format(policy.pricePerCycle);
  } catch (_) { return null; }
};

export const logicalCycleDates = (rows) => {
  if (!Array.isArray(rows)) return null;
  const seen = new Set(), result = [];
  for (const row of rows) {
    const start = typeof row?.startTime === 'string' && row.startTime.trim() ? Date.parse(row.startTime) : NaN;
    const end = typeof row?.endTime === 'string' && row.endTime.trim() ? Date.parse(row.endTime) : NaN;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || seen.has(start)) return null;
    seen.add(start);
    // One row is one logical lesson. Nested 25-minute segments never add units.
    result.push({ ...row, startTime: new Date(start).toISOString(), endTime: new Date(end).toISOString() });
  }
  return result.sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime));
};

export const cycleDateLabel = (slot, language, timezone) => {
  try {
    return new Intl.DateTimeFormat(cycleIntlLocale(language), {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: timezone,
    }).format(new Date(slot.startTime));
  } catch (_) { return slot.startTime; }
};

// Explicit, price-free presentation path for a child's READ-ONLY schedule.
// It does not grant booking, rescheduling, series editing or purchase rights.
// Payer pages must keep using the strict subscriptionBillingPolicy above.
export const childSubscriptionSchedulePolicy = (record) => {
  try {
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      throw new Error('INVALID_CHILD_SCHEDULE_POLICY');
    }
    if (provided(record.planSnapshot) && (typeof record.planSnapshot !== 'object' || Array.isArray(record.planSnapshot))) {
      throw new Error('INVALID_CHILD_SCHEDULE_POLICY');
    }
    const version = readSnapshotField(record, 'billingPolicyVersion');
    const mode = readSnapshotField(record, 'billingMode');
    const cycle = version === LESSON_CYCLE_POLICY;
    if (![LESSON_CYCLE_POLICY, CALENDAR_MONTH_POLICY].includes(version)) {
      throw new Error('UNSUPPORTED_BILLING_POLICY');
    }
    if (cycle ? mode !== 'lesson_cycle' : !['calendar_month', 'monthly'].includes(mode)) {
      throw new Error('BILLING_POLICY_MODE_MISMATCH');
    }
    const lessonsPerCycle = readSnapshotField(record, 'lessonsPerCycle')
      ?? (cycle ? undefined : readSnapshotField(record, 'lessonsPerMonth'));
    const lessonsPerWeek = readSnapshotField(record, 'lessonsPerWeek')
      ?? (cycle ? undefined : readSnapshotField(record, 'averageLessonsPerWeek'));
    const lessonDurationMinutes = readSnapshotField(record, 'lessonDurationMinutes');
    if (!positiveInteger(lessonsPerCycle, 124) || !positiveInteger(lessonsPerWeek, 12)) {
      throw new Error('INVALID_BILLING_POLICY_QUANTITY');
    }
    if (![25, 50].includes(lessonDurationMinutes)) {
      throw new Error('INVALID_LESSON_DURATION');
    }
    return { kind: cycle ? 'lesson_cycle' : 'calendar_month', billingPolicyVersion: version,
      billingMode: cycle ? 'lesson_cycle' : 'calendar_month', lessonsPerCycle, lessonsPerWeek,
      lessonDurationMinutes };
  } catch (error) {
    return { kind: 'invalid', errorCode: error.message };
  }
};

// Only stored weekly patterns are displayed. No dates, future occurrences,
// availability, money, entitlement or permission are inferred from a pattern.
export const childSubscriptionScheduleView = (record) => {
  const policy = childSubscriptionSchedulePolicy(record);
  const unavailable = { policy, status: 'unavailable', patterns: [] };
  if (policy.kind === 'invalid') return unavailable;
  const stored = record.schedulePreferences;
  if (!Array.isArray(stored) || stored.length > 12) return unavailable;
  const patterns = [];
  let malformed = false;
  for (const row of stored) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) { malformed = true; continue; }
    if (row.active === false) continue;
    if ((provided(row.active) && row.active !== true)
      || !Number.isInteger(row.dayOfWeek) || row.dayOfWeek < 0 || row.dayOfWeek > 6
      || typeof row.time !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(row.time)
      || (provided(row.lessonDurationMinutes) && row.lessonDurationMinutes !== policy.lessonDurationMinutes)) {
      malformed = true;
      continue;
    }
    patterns.push({ dayOfWeek: row.dayOfWeek, time: row.time,
      timezone: typeof row.timezone === 'string' && row.timezone.length <= 100 ? row.timezone : '',
      teacherName: typeof row.teacherName === 'string' && row.teacherName.length <= 200 ? row.teacherName : '',
      lessonDurationMinutes: policy.lessonDurationMinutes });
  }
  return { policy, status: malformed ? (patterns.length ? 'partial' : 'unavailable')
    : (patterns.length ? 'available' : 'empty'), patterns };
};
